import type { Bet } from 'src/types/domain/Bet'
import type { BetStatus } from 'src/types/domain/BetStatus'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import { selectRoundOdds } from 'src/components/BirdieRoundCard/roundOdds'
import { puttResultVisible } from './boardPhase'
import { golferInitials } from './capsuleState'
import { birdiePastRound, type BirdieRoundBoost } from './pastRounds'
import type { PuttMark } from './tileViewModel'

/**
 * The bet history's Birdie shape (B26): ONE entry per round, holding the
 * individual bets placed on that round — a bet slip (B26d).
 *
 * A player can back several options in one round — that is the whole point of
 * the board — and the flat list files each chip as its own unrelated entry,
 * repeating the round id, the time and the round's facts once per chip. Four
 * chips on one putt sequence read as four different games.
 *
 * WHY THIS IS A PURE TRANSFORM AND NOT A FETCH. `BetHistoryPanelContainer`
 * takes the whole list out of the store (`useGetBetsByGameAndPlayer`), which
 * `usePlayerBets` fills from ONE channel push. There is no server PAGING — no
 * cursor, no second request — so the pane's "Load More" is a client-side
 * slice, which becomes a slice of ROUNDS and changes in no other way.
 *
 * 🚨 THE PUSH IS CAPPED, THOUGH, and an earlier draft of this comment said it
 * was not. `client:player:bets:get` runs `GetPlayerBetsUseCase`, which pins
 * `betLimit := int32(200)` and reads the pk2sk2 index newest-first: the
 * player's 200 most recent bets across ALL games, which the store then filters
 * down by `raceConfigurationId`. So the OLDEST round the client can ever see
 * may be bisected by that cap, and its group holds only the bets that made the
 * cut. A flat list truncated the same way and merely showed fewer honest rows;
 * a GROUP sums, so that one round's `betCount` / `totalStaked` /
 * `totalReturned` can understate.
 *
 * Nothing here can detect it: the count is spent before this function sees a
 * bet (the list is already game-filtered, and the store merges live arrivals
 * on top), so the raw snapshot's length would have to be threaded down from
 * `usePlayerBets`. Recorded rather than papered over — the honest fix is a
 * `capped` flag from the fetch, and it is outside this feature's blast radius.
 *
 * Nothing here knows about React, and nothing here formats money: the sums are
 * raw numbers so the pane can render them through the SAME `displayMoney` /
 * `getCurrencySymbol` helpers every other game's row uses. That split is
 * load-bearing — see `totalReturned`. The two money helpers at the bottom
 * take strings the pane has ALREADY formatted, for the same reason.
 */

/**
 * One bet's state, as the slip words it — the four things a row can end in.
 *
 * `won` and `cashed_out` are one word (money came back); `cancelled` is
 * `void`, its own word, never a loss: voiding a WHOLE round is a routine
 * operation on this platform, and a refunded stake called lost money is the
 * one thing B26 was told never to print.
 */
export type BirdieBetState = 'won' | 'lost' | 'pending' | 'void'

export const betState = (status: BetStatus): BirdieBetState =>
  status === 'won' || status === 'cashed_out' ? 'won' : status === 'cancelled' ? 'void' : status

/**
 * The entry's verdict — the chip in the eyebrow, and the word in the totals
 * row when nothing came back. It is not any one bet's state: a round with
 * three bets can hold a winner and two losers at once.
 */
export type BirdieEntryVerdict = BirdieBetState

export interface BirdieBetRound {
  /** The grouping key — every bet carries the round it was placed on. */
  raceId: string
  /** The NEWEST bet's `createdAt` in the group: the row's time and its order. */
  placedAt: string
  /** The round's bets, newest first. Never empty. */
  bets: Bet[]
  betCount: number
  /** Sum of `betAmount`. RAW — the caller formats it. */
  totalStaked: number
  /**
   * Sum of `potentialPayout` over WON / CASHED OUT bets only, gross (owner,
   * 2026-09-09: "that's how other games do it right?" — yes, the flat row's
   * `Won Amount` renders `potentialPayout`, `betUtils.getVisualPotentialPayout`).
   *
   * 🚨 RAW, and summed exactly once. A decimal sum in IEEE-754 does not land on
   * the cent: two 15.07 payouts add to 30.140000000000004, and a row that
   * formatted each bet and added the strings — or added and then printed with
   * `toFixed` — would show a figure the wallet never moved. `displayMoney` runs
   * the total through `roundAmount`, which snaps the dust at 15 significant
   * digits before truncating, so ONE sum formatted ONCE is right and anything
   * else is luck. Pinned in `betHistoryRounds.test.ts`.
   */
  totalReturned: number
  /**
   * The slip's rule (B26d): any won → won; else any pending → pending; else
   * all void → void; else lost. A winner outranks a bet still running — the
   * entry already reads as settled, and the totals row says how many are
   * still open (`pendingCount`) rather than calling the whole round pending.
   */
  verdict: BirdieEntryVerdict
  /** Bets still running — the note under the totals label when it is not 0. */
  pendingCount: number
}

/** `createdAt` in ms, with an unparseable stamp sorted last rather than NaN. */
const placedMs = (bet: Bet): number => {
  const ms = new Date(bet.createdAt).getTime()
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * The player's bets, grouped into rounds — newest round first, and newest bet
 * first inside each round.
 *
 * A bet with no `raceId` is dropped rather than gathered under an empty key:
 * it belongs to no round, so it has no round row to open, and the one thing
 * worse than not showing it would be showing it as a round of its own that
 * names nothing.
 */
export function groupBetsByRound(bets: readonly Bet[]): BirdieBetRound[] {
  const byRace = new Map<string, Bet[]>()
  for (const bet of bets) {
    if (!bet.raceId) continue
    const existing = byRace.get(bet.raceId)
    if (existing === undefined) byRace.set(bet.raceId, [bet])
    else existing.push(bet)
  }

  const rounds: BirdieBetRound[] = []
  byRace.forEach((group, raceId) => {
    const ordered = [...group].sort((a, b) => placedMs(b) - placedMs(a))
    const newest = ordered[0]
    if (newest === undefined) return

    let totalStaked = 0
    let totalReturned = 0
    let anyWon = false
    let pendingCount = 0
    // Every bet voided, which is what a whole-round void looks like from here.
    // Deliberately `every`, not "no losers": one unrecognised status alongside
    // a void is not a voided round, and falling back to lost is the
    // conservative answer.
    let allVoid = true
    for (const bet of ordered) {
      // Cancelled stakes stay IN the total. The label is what was staked, the
      // flat row prints a voided bet's own amount the same way, and the row's
      // own return cell says the money came back.
      totalStaked += bet.betAmount
      const state = betState(bet.status)
      if (state === 'won') {
        totalReturned += bet.potentialPayout
        anyWon = true
      }
      if (state === 'pending') pendingCount += 1
      if (state !== 'void') allVoid = false
    }

    rounds.push({
      raceId,
      placedAt: newest.createdAt,
      bets: ordered,
      betCount: ordered.length,
      totalStaked,
      totalReturned,
      verdict: anyWon ? 'won' : pendingCount > 0 ? 'pending' : allVoid ? 'void' : 'lost',
      pendingCount
    })
  })

  rounds.sort((a, b) => {
    const bMs = new Date(b.placedAt).getTime()
    const aMs = new Date(a.placedAt).getTime()
    return (Number.isNaN(bMs) ? 0 : bMs) - (Number.isNaN(aMs) ? 0 : aMs)
  })
  return rounds
}

/**
 * The entry's head — who was putting, and (once it may be shown) what
 * happened — plus the round's Lightning facts once it has settled.
 *
 * Two different clocks, on purpose:
 *
 *  - the GOLFER comes off `race_ready`'s committed card, so they are known
 *    from the moment betting opens and a pending round's entry is not a blank
 *    strip;
 *  - the RESULT is withheld until the round's own reveal hold has elapsed, the
 *    same predicate and the same constant the past-rounds strip and the board's
 *    capsule use (`boardPhase.puttResultVisible`). `race_settled` lands on the
 *    EVENT clock, which leads the viewer's frames by their playout buffer, so a
 *    row that drew the balls the moment the event arrived would print the third
 *    putt's result beside a clip still showing the ball in the air. The bet
 *    history is a panel over the same live stream; it gets the same rule.
 *
 * The Lightning facts are the round card's own (`birdiePastRound`), and so
 * exist only once the round has SETTLED: the boost is not a spoiler of the
 * result — the live board shows it the moment betting closes — but the reader
 * that prices it both ways is the settled-round reader, and a second one here
 * would be a second place for the two prices to disagree.
 */
export interface BirdieRoundHead {
  golferName: string
  initials: string
  headshotUrl?: string
  /** The round's make chance per putt, as a FRACTION — the caller formats it. */
  makeRate: number
  /** The three putts, in putt order. EMPTY until the reveal hold has elapsed. */
  marks: PuttMark[]
  /** `IN2` — the count option that came up. Withheld with the marks. */
  countKey?: string
  /** `HMH` — the exact pattern that came up. Withheld with the marks. */
  patternKey?: string
  /** True once the round has settled as a Lightning round. */
  lightning: boolean
  /** The boosted options, priced both ways, once settled. Empty otherwise. */
  boosts: BirdieRoundBoost[]
}

export function birdieRoundHead(input: {
  race: Race | undefined
  marbles: readonly Pick<Marble, 'uuid' | 'color'>[]
  nowMs: number
}): BirdieRoundHead | undefined {
  const { race, marbles, nowMs } = input
  const odds = selectRoundOdds(race)
  if (odds === undefined) return undefined

  const { golfer, makeRatePpm } = odds.card
  const settled = race === undefined ? undefined : birdiePastRound(race, marbles)
  const revealed =
    settled !== undefined && puttResultVisible({ settledAt: settled.settledAtMs, now: nowMs })

  return {
    golferName: golfer.name,
    initials: golferInitials(golfer.name),
    ...(golfer.headshotUrl === undefined ? {} : { headshotUrl: golfer.headshotUrl }),
    makeRate: makeRatePpm / 1_000_000,
    marks: revealed ? settled.marks : [],
    ...(revealed && settled.countKey !== undefined ? { countKey: settled.countKey } : {}),
    ...(revealed && settled.patternKey !== undefined ? { patternKey: settled.patternKey } : {}),
    lightning: settled !== undefined && settled.lightning,
    boosts: settled === undefined ? [] : settled.boosts
  }
}

/**
 * Whether the entry's VERDICT is withheld with the result (B26d review).
 *
 * The bets flip to won on the settlement push, on the same event clock as
 * `race_settled`, so for the length of the reveal hold the store already
 * knows the result the head is hiding — and a gold bar over three hidden
 * putts is the exact frame the hold exists to protect. So the chip, every
 * row's return and the totals read as still running until the marks may be
 * drawn: the same predicate and the same constant as `birdieRoundHead`.
 *
 * Three edges, each decided on purpose:
 *
 *  - NOT HYDRATED (no race): nothing is known about the clip, so there is
 *    nothing to wait for. The wallet's word stands, as the flat row prints it
 *    — a panel opened on old rounds must not read pending until Supabase
 *    answers.
 *  - HYDRATED BUT NOT SETTLED, with a won or lost verdict: the pane's race is
 *    lagging the push (a bet resolves the moment the round settles; the
 *    settled race lands on the next hydration, throttled). That settlement is
 *    seconds old at most, so the entry is held until the race catches up —
 *    rather than flashing won, then pending for the rest of the hold, then
 *    won again.
 *  - PENDING or VOID: nothing to withhold. A pending entry already says so; a
 *    voided round has no result to spoil and never settles, so held it would
 *    read pending for ever.
 *
 * A settled stamp the clock cannot read is treated as not hydrated: better
 * the wallet's word than a verdict withheld until the page reloads.
 */
export function resultWithheld(input: {
  race: Race | undefined
  verdict: BirdieEntryVerdict
  nowMs: number
}): boolean {
  const { race, verdict, nowMs } = input
  if (race === undefined) return false
  if (verdict === 'pending' || verdict === 'void') return false
  const settled = race.raceEvents.find((event) => event.eventType === 'race_settled')
  if (settled === undefined) return true
  const settledAt = new Date(settled.eventTimestamp).getTime()
  if (Number.isNaN(settledAt)) return false
  return !puttResultVisible({ settledAt, now: nowMs })
}

/**
 * The slip's money rule (B26d), on strings the pane has ALREADY formatted.
 *
 * The pane prints money as symbol-then-amount, and a currency with no glyph
 * uses its ISO code as the symbol — so a slip that treated every symbol as one
 * glued token would print `Rp35,180,000.00` as a single 15-character word that
 * cannot break, and the column would clip it. An ALPHABETIC symbol of two or
 * more letters (Rp, kRp, CHF, GC, SC, KSh) is therefore its own token beside
 * the figure; a glyph ($, €, ₮, R$) stays glued, as every other row prints it.
 */
export interface SlipMoney {
  /** The currency code, when the symbol is alphabetic. */
  code?: string
  /** The figure — with the glyph glued to it when there is no code. */
  figure: string
}

const ALPHABETIC_SYMBOL = /^[A-Za-z]{2,}$/

export function slipMoney(symbol: string, amount: string): SlipMoney {
  return ALPHABETIC_SYMBOL.test(symbol)
    ? { code: symbol, figure: amount }
    : { figure: `${symbol}${amount}` }
}

/**
 * The stacking threshold: code plus figure, in characters, no space.
 *
 * MEASURED, not chosen. B26d measured 12 on the display face; B26e moved the
 * figures onto the page's narrower body face and measured again, on the real
 * component in headless Chrome at the sidebar's 314 px of content
 * (`docs/superpowers/specs/birdie-b26d/proof/threshold.json`): the widest
 * entry the fixtures can draw — an exact pick's tag beside a Lightning-boosted
 * three-digit price wearing its seal, the stake and the return both at the
 * candidate's length, so the totals too — keeps every figure on one line up
 * to ELEVEN characters and wraps a row at twelve (2.2 px short), so the
 * threshold stays 12. The lighter shapes would allow more (13 with the exact
 * tag beside the fixtures' own odds, 15 with an any-order pick beside an
 * unboosted price), but the grid's tracks are shared by every row of the
 * entry, so the widest row shape governs: the narrower face's gain is spent
 * by the seal and the tag. Re-derive here, not by eye, if either changes.
 */
export const SLIP_STACK_CHARS = 12

/**
 * Whether the entry stacks every code under its figure (`--stacked`).
 *
 * ONE rhythm for the whole grid, decided ONCE from every money string it
 * carries — stakes, payouts and totals: the moment any one of them is too
 * wide for its line (code plus figure at `SLIP_STACK_CHARS` characters or
 * more, measured on the real column), every figure in the grid takes the
 * stacked form, so no two rows of the same slip print the same currency two
 * ways. The unfolded facts are not the grid: they keep the inline form.
 */
export const slipStacked = (symbol: string, amounts: readonly string[]): boolean =>
  ALPHABETIC_SYMBOL.test(symbol) &&
  amounts.some((amount) => symbol.length + amount.length >= SLIP_STACK_CHARS)
